import { MozartArc, SpritzArc } from '@arcxgame/contracts'
import { TransactionOverrides } from '@arcxgame/contracts/dist/arc-types/ethereum'
import {
  BaseERC20Factory,
  MozartSavingsV1Factory,
  TestTokenFactory,
} from '@arcxgame/contracts/dist/src/typings'
import { getErc20Details } from '@arcxmoney/arcx-react'
import { BigNumber, BigNumberish, constants, Contract, ContractTransaction, Signer } from 'ethers'
import { normalizeToWei } from 'src/utils/normalizeToWei'

export class ArcService {
  signer: Signer

  constructor(public readonly spritz: SpritzArc, public readonly mozart: MozartArc) {
    this.signer = mozart.signer
  }

  getArc(name: string) {
    if (name === 'LINKUSD') {
      return this.spritz
    } else {
      return this.mozart
    }
  }

  async mintTestCollateral(name: string, amount: BigNumberish, account: string) {
    if (name === 'LINKUSD') {
      const token = TestTokenFactory.connect(this.spritz.collateralAsset.address, this.signer)
      return token.mintShare(account, amount)
    } else {
      const synth = this.mozart.synths[name]
      const token = TestTokenFactory.connect(synth.collateral.address, this.signer)
      const { hash, wait } = await token.mintShare(account, amount, {
        gasLimit: '250000',
      })
      console.log({ hash })
      return wait()
    }
  }

  async approveStableShare(
    amount: BigNumberish,
    name: string,
    caller?: Signer,
    overrides: TransactionOverrides = {},
  ) {
    if (name === 'LINKUSD') {
      return this.spritz.approveStableShare(amount, caller, overrides)
    } else {
      const synth = this.mozart.synths[name]
      return this.mozart.approveCollateral(amount, caller, synth, overrides)
    }
  }

  async borrow(
    positionId: BigNumberish,
    collateralAmount: BigNumberish,
    borrowAmount: BigNumberish,
    name: string,
    caller?: Signer,
    overrides: TransactionOverrides = {},
  ) {
    if (name === 'LINKUSD') {
      return this.spritz.borrow(positionId, collateralAmount, borrowAmount, caller, overrides)
    } else {
      return this.mozart.borrow(
        positionId,
        collateralAmount,
        borrowAmount,
        caller,
        this.mozart.synths[name],
        overrides,
      )
    }
  }

  async openPosition(
    collateralAmount: BigNumberish,
    borrowAmount: BigNumberish,
    name: string,
    caller?: Signer,
    overrides?: TransactionOverrides,
  ) {
    await this.approveStableShare(collateralAmount, name)

    if (name === 'LINKUSD') {
      return this.spritz.openPosition(collateralAmount, borrowAmount, caller, overrides)
    } else {
      const result = await this.mozart.openPosition(
        collateralAmount,
        BigNumber.from(borrowAmount),
        caller,
        this.mozart.synths[name],
        overrides,
      )
      return result
    }
  }

  repay(
    positionId: BigNumberish,
    repaymentAmount: BigNumberish,
    withdrawAmount: BigNumberish,
    name: string,
    caller?: Signer,
    overrides?: TransactionOverrides,
  ) {
    if (name === 'LINKUSD') {
      return this.spritz.repay(positionId, repaymentAmount, withdrawAmount, caller, overrides)
    } else {
      return this.mozart.repay(
        positionId,
        repaymentAmount,
        withdrawAmount,
        caller,
        this.mozart.synths[name],
        overrides,
      )
    }
  }

  async approveSynthetic(
    amount: BigNumberish,
    name: string,
    caller?: Signer,
    overrides?: TransactionOverrides,
    spender?: string,
  ) {
    if (name === 'LINKUSD') {
      return this.spritz.approveSynthetic(amount, caller, overrides)
    } else {
      const synth = this.mozart.synths[name]
      return this.mozart.approveSynthetic(amount, caller, synth, spender, overrides)
    }
  }

  liquidatePosition(
    positionId: BigNumberish,
    name: string,
    caller?: Signer,
    overrides?: TransactionOverrides,
  ) {
    if (name === 'LINKUSD') {
      return this.spritz.liquidatePosition(positionId, caller, overrides)
    } else {
      return this.mozart.liquidatePosition(positionId, caller, this.mozart.synths[name], overrides)
    }
  }

  async stakeSavings(
    savingContractAddress: string,
    amount: BigNumberish,
    name: string,
    overrides: TransactionOverrides = {},
  ) {
    await this.approveSynthetic(amount, name, undefined, overrides, savingContractAddress)
    const savingContract = MozartSavingsV1Factory.connect(savingContractAddress, this.signer)
    return savingContract.stake(amount, { ...overrides, gasLimit: 300000 })
  }

  async withdrawSavings(
    savingContractAddress: string,
    amount: BigNumberish,
    overrides: TransactionOverrides = {},
  ) {
    const savingContract = MozartSavingsV1Factory.connect(savingContractAddress, this.signer)
    return savingContract.unstake(amount, { ...overrides, gasLimit: 150000 })
  }

  async unstakeAllSavings(
    savingContractAddress: string,
    overrides: TransactionOverrides = {},
  ): Promise<ContractTransaction> {
    const savingContract = MozartSavingsV1Factory.connect(savingContractAddress, this.signer)
    return savingContract.unstakeAll(overrides)
  }

  async getUniswapRatioDetails(uniswapAddress: string) {
    const contract = new Contract(
      uniswapAddress,
      [
        'function token0() external view returns (address)',
        'function token1() external view returns (address)',
      ],
      this.signer,
    )
    const token0 = BaseERC20Factory.connect(await contract.token0(), this.signer)
    const token1 = BaseERC20Factory.connect(await contract.token1(), this.signer)
    const token0Balance = await token0.balanceOf(uniswapAddress)
    const token1Balance = await token1.balanceOf(uniswapAddress)
    const token0Details = await getErc20Details(token0)
    const token1Details = await getErc20Details(token1)
    return {
      name: `${token0Details.symbol}/${token1Details.symbol}`,
      ratio: constants.WeiPerEther.mul(normalizeToWei(token0Balance, token0Details.decimals)).div(
        normalizeToWei(token1Balance, token1Details.decimals),
      ),
    }
  }
}
